import React, { useState } from 'react';

import { MoreHoriz } from '@mui/icons-material';
import {
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import classNames from 'classnames';

import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { ReactComponent as ViewDetailsIcon } from 'new/components/icons/fi-rr-eye.svg';

import type {
  CapsuleRowMenuAction,
  CapsuleTableDataRowProps,
  RowActionsProps,
} from 'new/modules/settings/capsules/viewModels/capsulesTableViewModel';

import styles from 'new/modules/settings/capsules/screens/CapsulesSettingsScreen/components/CapsulesTable/CapsulesTableDataRow/CapsulesTableDataRow.module.scss';

const RowActions: React.FC<RowActionsProps> = ({ capsuleRow, onRowMenuAction }) => {
  const [menuAnchorElement, setMenuAnchorElement] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorElement);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorElement(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorElement(null);
  };

  const handleMenuItemSelect = (action: CapsuleRowMenuAction) => {
    handleMenuClose();
    onRowMenuAction?.(action, capsuleRow);
  };

  return (
    <>
      <IconButton
        aria-label="Actions"
        onClick={handleMenuOpen}
        size="small"
        className={styles.actionsIconButton}
      >
        <MoreHoriz fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={menuAnchorElement}
        open={isMenuOpen}
        onClose={handleMenuClose}
        onClick={(clickEvent) => clickEvent.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            className: styles.menuPaper,
          },
        }}
      >
        <MenuItem
          onClick={() => handleMenuItemSelect('viewDetails')}
          className={styles.menuItemWithIcon}
        >
          <ListItemIcon className={styles.menuListItemIcon}>
            <ViewDetailsIcon className={styles.menuActionIcon} />
          </ListItemIcon>
          View Details
        </MenuItem>
        <Divider component="li" className={styles.menuDivider} />
        <MenuItem
          onClick={() => handleMenuItemSelect('delete')}
          className={classNames(styles.menuItemWithIcon, styles.menuItemDanger)}
        >
          <ListItemIcon className={classNames(styles.menuListItemIcon, styles.menuDangerIcon)}>
            <DeleteIcon className={styles.menuActionIcon} />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export const CapsuleTableDataRow: React.FC<CapsuleTableDataRowProps> = ({
  capsuleRow,
  onRowClick,
  onRowMenuAction,
  showRowActions = true,
}) => {
  const hasRowClickHandler = Boolean(onRowClick);

  const goToDetails = () => {
    if (!onRowClick) return;
    onRowClick(capsuleRow);
  };

  return (
    <TableRow
      className={classNames(
        styles.tableRow,
        hasRowClickHandler ? styles.tableRowClickable : styles.tableRowStatic,
      )}
      onClick={goToDetails}
    >
      <TableCell className={styles.dataCell}>
        <Typography variant="body2" className={styles.nameTitle}>
          {capsuleRow.name}
        </Typography>
      </TableCell>
      <TableCell className={styles.dataCell}>
        <Typography variant="body2" className={styles.uidText}>
          {capsuleRow.uid ?? '-'}
        </Typography>
      </TableCell>
      <TableCell className={styles.dataCell}>
        <Typography variant="body2" className={styles.mutedBodyText}>
          {capsuleRow.registeredAt}
        </Typography>
      </TableCell>
      <TableCell className={styles.actionsCell}>
        {showRowActions ? (
          <RowActions capsuleRow={capsuleRow} onRowMenuAction={onRowMenuAction} />
        ) : null}
      </TableCell>
    </TableRow>
  );
};
